import { Component, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AccessGroup as AccessGroupUser, AccessGroupUsersSearchEvent, ChargingUser } from '../charging-user.model';
import { SnackbarService, FeedbackInfoComponent } from 'ok-angular-shared/components';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { PageEvent } from '@angular/material/paginator';
import { BehaviorSubject, combineLatest, debounceTime, map, Observable, shareReplay, startWith, switchMap, tap } from 'rxjs';
import { DiscountType } from 'src/app/shared/enums/discount-type.enum';
import { AbstractControl, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { AccessGroup, AccessGroupSearchEvent } from '../../access-groups/access-group.model';
import { Guid } from 'guid-typescript';
import moment from 'moment';
import { MatSelectChange } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { DkCurrencyPipe } from '../../../shared/pipes/dk-currency';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { MatPaginatorModule } from '@angular/material/paginator';
import { AccessGroupCarType } from 'src/app/shared/enums/access-group-car-type';
import { IdTagUserType } from 'src/app/shared/enums/id-tag-user-type.enum';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AccessGroupUserType } from "../../../shared/enums/access-group-user-type";

@Component({
  selector: 'adm-charging-user-accessgroups-modal',
  templateUrl: './charging-user-accessgroups-modal.component.html',
  styleUrl: './charging-user-accessgroups-modal.component.scss',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatPaginatorModule,
    MatTooltipModule,
    DkCurrencyPipe,
    CustomDropDownArrowDirective,
    FeedbackInfoComponent,
  ],
})
export class ChargingUserAccessGroupsModalComponent implements OnInit {
  dialogRef = inject<MatDialogRef<ChargingUserAccessGroupsModalComponent>>(MatDialogRef);
  data = inject<{
    chargingUser: ChargingUser;
  }>(MAT_DIALOG_DATA);
  private chargingUserService = inject(ChargingUserService);
  private accessGroupService = inject(AccessGroupService);
  private snackbarService = inject(SnackbarService);

  public chargingUser: ChargingUser;
  public chargingUserUpdated: ChargingUser | null = null;
  public accessGroupUsers$: Observable<AccessGroupUser[]>;
  public accessGroups$: Observable<AccessGroup[]>;
  public filteredAccessGroups$: Observable<AccessGroup[]>;
  public accessGroupsAvailable$: Observable<AccessGroup[]>;
  public discountType: typeof DiscountType = DiscountType;
  public accessGroupUserType: typeof AccessGroupUserType = AccessGroupUserType;
  public formGroup: FormGroup;
  public saving = false;
  public refreshSubject = new BehaviorSubject(null);
  public accessGroupUserAddedIdentifier: string | null;
  public accessGroupUserCreateModel: FormGroup = new FormGroup(
    {
      typeAheadQuery: new FormControl<string | null | { identifier: string; name: string }>(null, { validators: [Validators.required] }),
      timeLimited: new FormControl({ value: null, disabled: true }, { validators: [Validators.required] }),
      startDate: new FormControl<Date | null>(null),
      endDate: new FormControl<Date | null>(null),
    },
    { validators: [this.timeLimitedValidator] }
  );

  private timeLimitedValidator(group: AbstractControl): { [key: string]: any } | null {
    const timeLimited = group.get('timeLimited')?.value;
    const startDate = group.get('startDate')?.value as Date;
    const endDate = group.get('endDate')?.value as Date;

    if (timeLimited && (!startDate || !endDate)) {
      return { datePickerInvalid: true };
    }
    return null;
  }

  async ngOnInit() {
    this.chargingUser = this.data?.chargingUser;
    this.formGroup = this.initFormGroup();

    const pageEvent = new PageEvent();
    pageEvent.pageSize = 1000;
    const searchEvent = new AccessGroupUsersSearchEvent(pageEvent);
    searchEvent.chargingUserIdentifier = this.chargingUser.identifier;
    this.accessGroupUsers$ = this.refreshSubject.pipe(
      switchMap(() => this.chargingUserService.getAccessGroupUsers(searchEvent)),
      map(result => result.items),
      shareReplay(1)
    );

    const searchEventAccessGroup = new AccessGroupSearchEvent(pageEvent);
    searchEventAccessGroup.organizationId = this.chargingUser.organizationId;
    searchEventAccessGroup.carType = this.mapIdTagUserTypeToCarType(this.chargingUser.idTagUserType);
    this.accessGroups$ = this.accessGroupService.getAccessGroups(searchEventAccessGroup).pipe(
      map(result => result.items),
      shareReplay(1)
    );

    this.accessGroupsAvailable$ = combineLatest([this.accessGroups$, this.accessGroupUsers$]).pipe(
      map(([accessGroups, accessGroupUsers]) => {
        if (accessGroupUsers) {
          return accessGroups.filter(o => !accessGroupUsers.some(u => u.identifier === o.identifier));
        } else {
          return accessGroups;
        }
      })
    );

    this.filteredAccessGroups$ = combineLatest([
      this.accessGroupsAvailable$,
      this.accessGroupUserCreateModel.controls.typeAheadQuery.valueChanges.pipe(
        startWith(this.accessGroupUserCreateModel.controls.typeAheadQuery.value),
        debounceTime(300)
      ),
    ]).pipe(
      tap(([_, accessQuery]) => {
        if (accessQuery && typeof accessQuery === 'object') {
          this.formGroup.controls.selectedAccessGroupIdentifier.setValue(accessQuery.identifier);
        } else {
          this.formGroup.controls.selectedAccessGroupIdentifier.setValue(null);
        }
      }),
      map(([accessGroups, accessQuery]) => {
        if (!accessQuery || typeof accessQuery === 'object') {
          return accessGroups;
        }
        return accessGroups.filter(o => o.name?.toLowerCase().indexOf(accessQuery.toLowerCase()) > -1);
      })
    );
  }

  get accessGroupTypeAheadQuery() {
    return this.accessGroupUserCreateModel.controls['typeAheadQuery'];
  }
  get accessGroupCreateTimelimited() {
    return this.accessGroupUserCreateModel.controls['timeLimited'];
  }
  get accessGroupCreateTimeFrom() {
    return this.accessGroupUserCreateModel.controls['startDate'];
  }
  get accessGroupCreateTimeTo() {
    return this.accessGroupUserCreateModel.controls['endDate'];
  }
  get selectedAccessGroupIdentifier() {
    return this.formGroup?.controls['selectedAccessGroupIdentifier'];
  }

  public resetAccessGroup() {
    this.accessGroupUserCreateModel.controls['timeLimited'].disable();
    this.resetFormControl(this.selectedAccessGroupIdentifier);
    this.resetFormControl(this.accessGroupTypeAheadQuery);
    this.resetFormControl(this.accessGroupCreateTimelimited);
    this.resetFormControl(this.accessGroupCreateTimeFrom);
    this.resetFormControl(this.accessGroupCreateTimeTo);
  }

  resetFormControl(control: AbstractControl): void {
    control.setValue(null);
    control.markAsUntouched();
    this.formGroup.patchValue({
      control: null,
    });
  }

  public resetDates() {
    this.accessGroupCreateTimeFrom.setValue(null);
    this.accessGroupCreateTimeTo.setValue(null);
  }

  onTimeLimitedChange(event: MatSelectChange): void {
    if (event.value === false) {
      this.accessGroupCreateTimeFrom.setValue(null);
      this.accessGroupCreateTimeTo.setValue(null);
    }
  }

  onAccessGroupChange(event): void {
    this.accessGroupUserCreateModel.controls['timeLimited'].enable();
  }

  public accessGroupDisplayFn(accessGroup: AccessGroup): string {
    return accessGroup && accessGroup.name ? accessGroup.name : '';
  }

  public initFormGroup() {
    return new FormGroup({
      selectedAccessGroupIdentifier: new FormControl<string | null>(null, [Validators.required]),
    });
  }

  public async deleteAccessGroupUser(accessGroup: AccessGroupUser) {
    this.chargingUserService.deleteAccessGroupUser(accessGroup.accessGroupChargingUserIdentifier).subscribe({
      next: () => {
        this.refreshSubject.next(null);
        this.chargingUserUpdated = this.chargingUser;
        this.accessGroupUserAddedIdentifier = null;
        this.snackbarService.success(`Adgangsgruppens tilknytning er blevet fjernet.`);
      },
      error: err => {
        this.snackbarService.error(`Der skete fejl under fjernelse af adgangsgruppe tilknytningen. (${err.error.code})`);
      },
    });
  }

  public async createAccessGroupUser() {
    this.chargingUserService
      .createAccessGroupUser({
        AccessGroupIdentifier: this.formGroup.controls.selectedAccessGroupIdentifier.value,
        ChargingUserIdentifier: this.chargingUser.identifier,
        TimeFrom:
          this.accessGroupCreateTimelimited.value && this.accessGroupCreateTimeFrom.value
            ? this.accessGroupCreateTimeFrom.value
            : undefined,
        TimeTo:
          this.accessGroupCreateTimelimited.value && this.accessGroupCreateTimeTo.value
            ? moment(this.accessGroupCreateTimeTo.value).endOf('day').toDate()
            : undefined,
      })
      .subscribe({
        next: (id: Guid) => {
          this.chargingUserUpdated = this.chargingUser;
          this.accessGroupUserAddedIdentifier = id.toString();
          this.snackbarService.success(`Adgangsgruppen blev tilknyttet ${this.chargingUser.name}`);
          this.resetAccessGroup();
          this.refreshSubject.next(null);
        },
        error: err => {
          this.snackbarService.error(`Der skete fejl under tilknytningen. (${err.error.code})`);
        },
      });
  }

  public close() {
    this.dialogRef.close({ chargingUserUpdated: this.chargingUserUpdated });
    this.chargingUserUpdated = null;
  }

  rangeFilter(date: any): boolean {
    const m = moment(date);
    return m.isAfter(moment().subtract(1, 'days'));
  }

  mapIdTagUserTypeToCarType(idTagUserType: IdTagUserType): AccessGroupCarType | null {
    switch (idTagUserType) {
      case IdTagUserType.CompanyCar:
        return AccessGroupCarType.CompanyCar;
      case IdTagUserType.NoCompanyCar:
        return AccessGroupCarType.PrivateCar;
      default:
        return null;
    }
  }
}
